import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  CreditCard,
  Loader2,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PlanBadge } from '@/components/billing/PlanBadge';
import { linkAwsMarketplace } from '@/api/marketplace';
import { queryKeys } from '@/api/queryKeys';
import { useAuthStore } from '@/store/authStore';
import { normalizePlanTier } from '@/types/billing';
import { ApiRequestError } from '@/types/api';
import type { LinkAwsMarketplaceResponse } from '@/types/marketplace';

const SUPPORT_EMAIL = 'support@gateco.ai';

interface ErrorCopy {
  title: string;
  message: string;
}

/** Map a link-attempt failure to buyer-facing copy, keyed by HTTP status. */
function errorCopyFor(error: unknown): ErrorCopy {
  const status = error instanceof ApiRequestError ? error.status : undefined;
  switch (status) {
    case 404:
      return {
        title: 'Registration not found',
        message:
          'We could not find your AWS Marketplace registration. The activation link may have expired or already been used. Return to AWS Marketplace and choose "Set up your account" again to get a fresh link.',
      };
    case 409:
      return {
        title: 'Already linked to another organization',
        message:
          'This AWS Marketplace subscription is already linked to a different Gateco organization. Sign in with that organization, or contact us and we can help move the subscription.',
      };
    case 422:
      return {
        title: 'No active entitlement yet',
        message:
          'We could not find an active AWS Marketplace entitlement for this subscription. Entitlements can take a few minutes to propagate after purchase, so please try again shortly.',
      };
    case 502:
      return {
        title: 'Could not reach AWS Marketplace',
        message:
          'We could not read your entitlement from AWS Marketplace. This is usually temporary, so please try again in a few minutes.',
      };
    default:
      return {
        title: 'Activation failed',
        message:
          'Something went wrong while activating your AWS Marketplace subscription. Please try again, or contact support if the problem persists.',
      };
  }
}

/** Shared chromeless page frame (matches the OAuth callback / welcome screens). */
function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-lg space-y-6 animate-fade-in">
        <div className="text-center space-y-1">
          <span className="text-2xl font-bold tracking-tight text-foreground">gateco</span>
          <p className="text-sm text-muted-foreground">AWS Marketplace</p>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Landing page for the AWS Marketplace SaaS contract flow.
 *
 * AWS redirects a buyer here as `/marketplace/aws?ref=<opaque_link_token>` after
 * purchase. This page (mounted behind `ProtectedRoute`, which carries the full
 * path + query through login) exchanges the `ref` for an entitlement exactly
 * once via `POST /api/marketplace/aws/link`, refreshes the cached entitlements,
 * and confirms the activated plan.
 */
export function MarketplaceAwsLandingPage() {
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref')?.trim() ?? '';

  const queryClient = useQueryClient();
  const setUser = useAuthStore((s) => s.setUser);

  const mutation = useMutation<LinkAwsMarketplaceResponse, unknown, string>({
    mutationFn: (linkRef: string) => linkAwsMarketplace(linkRef),
    onSuccess: (data) => {
      // Reflect the newly activated plan immediately (the store drives the
      // entitlement context + nav), then refetch the canonical `me` payload.
      const currentUser = useAuthStore.getState().user;
      if (currentUser?.organization) {
        setUser({
          ...currentUser,
          organization: {
            ...currentUser.organization,
            plan: normalizePlanTier(data.plan),
          },
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me });
    },
  });

  // Fire the link call exactly once on mount (guarded against StrictMode double
  // effects and re-renders). Never call the API when `ref` is missing.
  const started = useRef(false);
  const { mutate } = mutation;
  useEffect(() => {
    if (!ref || started.current) return;
    started.current = true;
    mutate(ref);
  }, [ref, mutate]);

  // ── Missing reference: hard error, no API call ──
  if (!ref) {
    return (
      <PageFrame>
        <Card>
          <CardHeader className="items-center text-center">
            <AlertTriangle className="h-10 w-10 text-status-warning" />
            <CardTitle className="text-xl">Missing subscription reference</CardTitle>
            <CardDescription>
              This page needs an AWS Marketplace reference to activate your subscription.
              Please start again from AWS Marketplace using the "Set up your account" button.
            </CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-3">
            <Button asChild className="w-full">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=AWS%20Marketplace%20activation%20help`}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Need help? Contact {SUPPORT_EMAIL}
            </a>
          </CardFooter>
        </Card>
      </PageFrame>
    );
  }

  // ── Success ──
  if (mutation.isSuccess) {
    const data = mutation.data;
    const tier = normalizePlanTier(data.plan);
    return (
      <PageFrame>
        <Card>
          <CardHeader className="items-center text-center">
            <CheckCircle2 className="h-10 w-10 text-status-allow" />
            <CardTitle className="text-xl">
              {data.already_linked ? 'Subscription already active' : 'Subscription activated'}
            </CardTitle>
            <CardDescription>
              {data.already_linked
                ? 'This AWS Marketplace subscription is already linked to your organization. Your plan is ready to use.'
                : 'Your AWS Marketplace subscription is now linked to your organization. Billing is handled through AWS Marketplace.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex items-center justify-center gap-2">
            <span className="text-sm text-muted-foreground">Active plan</span>
            <PlanBadge tier={tier} />
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button asChild className="w-full">
              <Link to="/dashboard">Go to dashboard</Link>
            </Button>
            <Button asChild variant="outline" className="w-full">
              <Link to="/usage-billing">
                <CreditCard className="mr-2 h-4 w-4" />
                View usage &amp; billing
              </Link>
            </Button>
          </CardFooter>
        </Card>
      </PageFrame>
    );
  }

  // ── Error ──
  if (mutation.isError) {
    const copy = errorCopyFor(mutation.error);
    const subject = encodeURIComponent(`AWS Marketplace activation help (${copy.title})`);
    return (
      <PageFrame>
        <Card>
          <CardHeader className="items-center text-center">
            <XCircle className="h-10 w-10 text-destructive" />
            <CardTitle className="text-xl">{copy.title}</CardTitle>
            <CardDescription>{copy.message}</CardDescription>
          </CardHeader>
          <CardFooter className="flex flex-col gap-3">
            <Button
              className="w-full"
              onClick={() => mutation.mutate(ref)}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Retrying…
                </>
              ) : (
                'Try again'
              )}
            </Button>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${subject}`}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Still stuck? Contact {SUPPORT_EMAIL}
            </a>
          </CardFooter>
        </Card>
      </PageFrame>
    );
  }

  // ── Loading (idle-before-fire and in-flight) ──
  return (
    <PageFrame>
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
          <div className="space-y-1">
            <p className="font-medium text-foreground">
              Activating your AWS Marketplace subscription…
            </p>
            <p className="text-sm text-muted-foreground">
              This only takes a moment. Please do not close this tab.
            </p>
          </div>
        </CardContent>
      </Card>
    </PageFrame>
  );
}
